import { useState } from "react";
import {
  AlertTriangle,
  CheckCircle,
  Circle,
  Eye,
  Info,
  Loader2,
  XCircle,
} from "lucide-react";
import type {
  BenchmarkCase,
  CaseDefinition,
  CaseRunResult,
  CaseStatus,
} from "../types";

export function isCaseModified(c: BenchmarkCase): boolean {
  const keys: (keyof CaseDefinition)[] = [
    "situation", "segment", "situationSummary", "expectedSuggestion", "personaDetail",
  ];
  return keys.some((k) => c.originalCase[k] !== c.modifiedCase[k]);
}

export function formatPercent(n: number | null): string {
  if (n === null) return "N/A";
  return `${(n * 100).toFixed(1)}%`;
}

export const METRIC_TOOLTIPS = {
  accuracy:
    "Accuracy = (TP + TN) / (TP + TN + FP + FN). How often the model is right overall — including correct recommendations and correctly giving no recommendation.",
  precision:
    "Precision = TP / (TP + FP). Of all recommendations the system made, what percentage were the correct expected product? Penalises wrong and unnecessary suggestions.",
  recall:
    "Recall = TP / (TP + FN). Of all the products the system was supposed to recommend, what percentage did it successfully find? Penalises missed recommendations.",
};

export function TH({ children }: { children: React.ReactNode }) {
  return (
    <th className="relative px-4 py-3 text-left text-xs font-medium text-brand-muted uppercase tracking-wider border-b border-brand-border bg-brand-card">
      <div className="absolute inset-0 bg-brand-primary opacity-10" />
      <span className="relative">{children}</span>
    </th>
  );
}

export function StatusIcon({ status }: { status: CaseStatus }) {
  switch (status) {
    case "running":
      return <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />;
    case "complete":
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    case "failed":
      return <XCircle className="w-4 h-4 text-red-500" />;
    case "abandoned":
      return <AlertTriangle className="w-4 h-4 text-amber-500" />;
    default:
      return <Circle className="w-4 h-4 text-brand-muted" />;
  }
}

export function ResultBadge({ result }: { result: CaseRunResult }) {
  if (result.status === "failed") {
    return (
      <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold bg-red-100 text-red-800">
        ERROR
      </span>
    );
  }
  if (result.status === "abandoned") {
    return (
      <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold bg-orange-100 text-orange-800">
        ABANDONED
      </span>
    );
  }
  if (result.isPass) {
    return (
      <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold bg-green-200 text-green-800">
        PASS
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold bg-amber-100 text-amber-800">
      FAIL
    </span>
  );
}

export function ConfigTooltip({ text }: { text: string }) {
  const [show, setShow] = useState(false);

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <Info className="w-3.5 h-3.5 text-brand-muted/60 cursor-help" />
      {show && (
        <span className="absolute bottom-full right-0 mb-2 w-80 px-3 py-2 text-xs text-left bg-gray-900 text-white rounded-lg shadow-lg z-50 pointer-events-none whitespace-normal">
          {text}
        </span>
      )}
    </span>
  );
}

export function ChangesWarning() {
  const [show, setShow] = useState(false);

  return (
    <span
      className="relative inline-flex"
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
    >
      <AlertTriangle className="w-4 h-4 text-amber-500 cursor-help" />
      {show && (
        <span className="absolute right-full top-1/2 -translate-y-1/2 mr-2 w-64 px-3 py-2 text-xs text-left bg-gray-900 text-white rounded-lg shadow-lg z-50 pointer-events-none whitespace-normal">
          This test case has been modified, view the configuration for details
        </span>
      )}
    </span>
  );
}

export function ViewConfigButton({ onClick }: { onClick: (e: React.MouseEvent) => void }) {
  const [show, setShow] = useState(false);

  return (
    <button
      onClick={onClick}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      className="relative p-1 rounded hover:bg-brand-primary/10 text-brand-muted hover:text-brand-foreground transition-colors"
    >
      <Eye className="w-4 h-4" />
      {show && (
        <span className="absolute right-full top-1/2 -translate-y-1/2 mr-2 w-max px-3 py-2 text-xs bg-gray-900 text-white rounded-lg shadow-lg z-50 pointer-events-none whitespace-nowrap">
          View configuration
        </span>
      )}
    </button>
  );
}

export function DetailRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-xs font-medium text-brand-muted uppercase tracking-wider mb-1">
        {label}
      </dt>
      <dd className="text-sm text-brand-foreground">{children}</dd>
    </div>
  );
}

export function MetricCard({ label, value, tooltip }: { label: string; value: string; tooltip?: string }) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <div className="bg-brand-card border border-brand-border rounded-lg p-4 text-center relative">
      <div className="text-2xl font-bold text-brand-foreground">{value}</div>
      <div className="text-xs text-brand-muted uppercase tracking-wider mt-1 flex items-center justify-center gap-1">
        {label}
        {tooltip && (
          <span
            className="relative inline-flex"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            <Info className="w-3.5 h-3.5 text-brand-muted/60 cursor-help" />
            {showTooltip && (
              <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-64 px-3 py-2 text-xs text-left normal-case tracking-normal bg-gray-900 text-white rounded-lg shadow-lg z-50 pointer-events-none">
                {tooltip}
              </span>
            )}
          </span>
        )}
      </div>
    </div>
  );
}

export function EarlyProvisionInfoModal({ onClose }: { onClose: () => void }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={onClose}
    >
      <div
        className="bg-brand-card border border-brand-border rounded-lg p-6 max-w-2xl w-full mx-4 shadow-xl max-h-[80vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-brand-foreground">
            Early Product Recommendation Provision
          </h2>
          <button
            onClick={onClose}
            className="text-brand-muted hover:text-brand-foreground transition-colors"
          >
            <XCircle className="w-5 h-5" />
          </button>
        </div>

        <div className="mb-4 flex gap-2 rounded-md border border-blue-300 bg-blue-50 px-4 py-2.5 text-sm text-blue-800">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            This is a <strong>configuration parameter</strong> that affects the
            end-user experience. It controls when product recommendations are
            surfaced during a conversation and when the conversation ends.
          </span>
        </div>

        <div className="space-y-5 text-sm text-brand-foreground">
          <section>
            <h3 className="font-semibold mb-1.5">
              What does the user see in each mode?
            </h3>
            <ul className="list-disc pl-5 space-y-1.5 text-brand-muted">
              <li>
                <strong className="text-brand-foreground">Enabled</strong> — as
                soon as any product recommendation is identified during the
                conversation, it is immediately presented to the user and the
                conversation ends. The user sees one (or a small batch of)
                recommendation(s) early on. This may mean that other recommendations that the user may qualify for are not surfaced to them.
              </li>
              <li>
                <strong className="text-brand-foreground">Disabled</strong> —
                the system continues the conversation until all relevant
                products have been fully assessed. All recommendations are then
                presented together at the end as a complete set.
              </li>
            </ul>
          </section>

          <section>
            <h3 className="font-semibold mb-1.5">
              Why might multiple recommendations appear at different points?
            </h3>
            <p className="text-brand-muted">
              Products are evaluated against the customer's situation
              progressively as more information is gathered. Different products
              may become eligible at different stages of the conversation — for
              example, an income-protection product might be identified early
              while a pension-related suggestion only emerges after further
              questioning. This means recommendations can surface at distinct
              points throughout the dialogue rather than all at once.
            </p>
          </section>

          <section>
            <h3 className="font-semibold mb-1.5">
              How does this impact evaluation metrics?
            </h3>
            <p className="text-brand-muted">
              When early provision is enabled, the conversation ends at the
              first recommendation. That recommendation may not be the expected
              product for the test case — the persona may genuinely qualify for
              other products first. This leads to more apparent failures and
              makes accuracy, precision, and recall figures unreliable.
              Consequently, <strong>aggregate metrics are hidden</strong> for
              runs with this setting enabled because they would be misleading.
              <br /><br />
              <strong> As a result, this should be disabled for evaluating accuracy, precision, and recall metrics.</strong>
            </p>
          </section>

          <section>
            <h3 className="font-semibold mb-1.5">
              Alternative approach
            </h3>
            <p className="text-brand-muted">
              Another valid configuration would be to pass recommendations out
              to the user as they appear <em>without</em> ending the
              conversation. This would let the user see each recommendation in
              real time while the system continues evaluating remaining
              products. The current toggle is one way to handle timing;
              different deployment scenarios may call for different strategies.
            </p>
          </section>
        </div>
      </div>
    </div >
  );
}

export function ModeToggle({
  isCustomMode,
  onToggle,
}: {
  isCustomMode: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="flex items-center gap-2.5">
      <span
        className={`text-sm transition-colors ${!isCustomMode ? "text-brand-foreground font-medium" : "text-brand-muted"}`}
      >
        Predefined
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={isCustomMode}
        onClick={onToggle}
        className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors ${isCustomMode ? "bg-brand-primary" : "bg-brand-border"}`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow-sm transition-transform ${isCustomMode ? "translate-x-[18px]" : "translate-x-[3px]"}`}
        />
      </button>
      <span
        className={`text-sm transition-colors ${isCustomMode ? "text-brand-foreground font-medium" : "text-brand-muted"}`}
      >
        Custom
      </span>
    </div>
  );
}

export function EarlyTerminationBanner({ variant = "present" }: { variant?: "present" | "past" }) {
  return (
    <div className="mb-4 flex gap-2 rounded-md border border-blue-300 bg-blue-50 px-4 py-2.5 text-sm text-blue-800">
      <Info className="w-4 h-4 shrink-0 mt-0.5" />
      <span>
        {variant === "past"
          ? "Early Product Recommendation Provision was enabled for this run. More failing test cases are expected because the first recommendation provided in the conversation is not necessarily the expected suggestion — the described personas may also fit other recommendations. As a result, metrics are not shown for these runs since they will be misleading."
          : "Early Product Recommendation Provision is enabled. More failing test cases are expected because the first recommendation provided in the conversation is not necessarily the expected suggestion — the described personas may also fit other recommendations. As a result, metrics are not shown for these runs since they will be misleading."}
      </span>
    </div>
  );
}
